import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/system";
import { motion } from "framer-motion";
import QRCode from "qrcode";
import LazyLoad from "react-lazyload";

import Underline from "../assets/site-images/vector.webp";
import GooglePlay from "../assets/site-images/googleplay.webp";
import AppStore from "../assets/site-images/appstore.webp";
import TruckVideo from "../assets/site-images/Truck_Freight.mp4";

const letterVariants = {
  initial: { y: 0, opacity: 0.8 },
  animate: { y: -5, opacity: 1 },
};

const HomeContainer = styled(Box)(({ theme }) => ({
  height: "100vh",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  backgroundColor: "#000",
  fontFamily: "inherit",
  position: "relative",
  overflow: "hidden",
  padding: "0 10%",
  [theme.breakpoints.down("md")]: {
    padding: "0 5%",
    justifyContent: "center",
    textAlign: "center",
  },
}));

const VideoBackground = styled("video")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "bottom",
  zIndex: 0,
});

const VideoOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.4)", // Dark overlay for readability
  zIndex: 1,
});

const ContentBox = styled(motion.div)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  textAlign: "left",
  maxWidth: "650px",
  zIndex: 2,
  fontFamily: "Roboto, sans-serif",
}));

const HomeTitle = styled(Typography)(({ theme }) => ({
  fontSize: "4.5rem",
  fontWeight: "bold",
  color: "#fff",
  lineHeight: "1.1",
  textShadow: "0 4px 20px rgba(0,0,0,0.5)",
  [theme.breakpoints.down("lg")]: { fontSize: "3.5rem" },
  [theme.breakpoints.down("md")]: { fontSize: "2.8rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.8rem" },
}));

const EasyTextContainer = styled(motion.div)({
  display: "inline-block",
  position: "relative",
});

const UnderlineImage = styled(motion.img)({
  position: "absolute",
  bottom: -5,
  left: 0,
  width: "100%",
  height: "auto",
  filter: "brightness(0) invert(1)", // Make white
});

const Letter = styled(motion.span)(({ theme }) => ({
  fontSize: "inherit",
  fontWeight: "bold",
  color: "#fff",
  display: "inline-block",
  [theme.breakpoints.down("md")]: { fontSize: "inherit" },
}));

const HomeSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  color: "rgba(255, 255, 255, 0.9)",
  marginTop: "20px",
  maxWidth: "500px",
  textShadow: "0 2px 10px rgba(0,0,0,0.3)",
  [theme.breakpoints.down("md")]: { fontSize: "1.1rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1rem" },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  gap: "15px",
  marginTop: "30px",
  [theme.breakpoints.down("md")]: {
    justifyContent: "center",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "center",
  },
}));

const StoreButton = styled(Button)({
  padding: "0",
  borderRadius: "8px",
  textTransform: "none",
  background: "transparent",
  "&:hover": { background: "transparent", transform: "scale(1.05)" },
  transition: "transform 0.2s ease",
});

const StoreIcon = styled("img")(({ theme }) => ({
  width: "160px",
  cursor: "pointer",
  [theme.breakpoints.down("sm")]: { width: "140px" },
}));

const QRCodeContainer = styled(Box)(({ theme }) => ({
  marginTop: "40px",
  backgroundColor: "#fff",
  padding: "12px",
  borderRadius: "12px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "fit-content",
  boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
  [theme.breakpoints.down("md")]: {
    margin: "40px auto 0",
  },
}));

const QRImage = styled("img")(({ theme }) => ({
  width: "110px",
  height: "110px",
}));

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [qrSrc, setQrSrc] = useState("");

  useEffect(() => {
    const generateQR = async () => {
      try {
        const url = "https://main.d2ydtp6drfczo.amplifyapp.com/";
        const qr = await QRCode.toDataURL(url);
        setQrSrc(qr);
      } catch (error) {
        console.error("Error generating QR Code", error);
      }
    };

    generateQR();
  }, []);

  return (
    <HomeContainer id="home">
      <VideoBackground autoPlay muted loop playsInline>
        <source src={TruckVideo} type="video/mp4" />
      </VideoBackground>
      <VideoOverlay />

      <ContentBox
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 1 }}
      >
        <HomeTitle variant="h1">
          Commercial vehicle 
            rental and sharing -
          fast<br /> and{" "}
          <EasyTextContainer>
            {Array.from("easy!").map((char, index) => (
              <Letter
                key={index}
                variants={letterVariants}
                initial="initial"
                animate="animate"
                transition={{
                  duration: 0.5,
                  delay: index * 0.1,
                  repeat: Infinity,
                  repeatType: "reverse",
                  repeatDelay: 4,
                }}
              >
                {char}
              </Letter>
            ))}
            <UnderlineImage
              src={Underline}
              alt="Underline"
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1, delay: 1 }}
            />
          </EasyTextContainer>
        </HomeTitle>

        <HomeSubtitle variant="body1">
          Download the RigZip app to access trucks, trailers, and
          commercial vehicles in minutes.
        </HomeSubtitle>

        {/* Download Section */}
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center", // Center buttons and QR relative to each other
          width: "fit-content", // Only take as much space as the buttons
          mt: 4,
          [theme.breakpoints.down("md")]: {
            margin: "40px 0 0 0", // Ensure it stays left-aligned in the flex flow
          }
        }}>
          {/* Store Buttons */}
          <ButtonContainer sx={{ justifyContent: "center", mt: 0, width: "100%" }}>
            <StoreButton
              href="https://play.google.com/store/apps/"
              target="_blank"
              rel="noopener"
            >
              <StoreIcon src={GooglePlay} alt="Google Play Store" />
            </StoreButton>
            <StoreButton
              href="https://www.apple.com/app-store/"
              target="_blank"
              rel="noopener"
            >
              <StoreIcon src={AppStore} alt="App Store" />
            </StoreButton>
          </ButtonContainer>

          {/* QR Code */}
          <QRCodeContainer sx={{ 
            mt: 2,
            alignSelf: "center"
          }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: "#000", 
                fontWeight: "bold", 
                fontSize: "0.75rem",
                mb: 1,
                textAlign: "center",
                display: "block",
                width: "100%"
              }}
            >
              Scan to Download Instantly.
            </Typography>
            {qrSrc && (
              <QRImage src={qrSrc} alt="QR Code" />
            )}
          </QRCodeContainer>
        </Box>
      </ContentBox>
    </HomeContainer>
  );
};

export default Home;
